import { lazy, Suspense } from "react";
import type { ArchiveTemplateModalProps } from "./ArchiveTemplateModal";
import type { CropImageModalProps } from "./CropImageModal";
import type { DiscardUnsavedChangesModalProps } from "./DiscardUnsavedChangesModal";
import type { SaveTemplateModalProps } from "./SaveTemplateModal";

const ArchiveTemplateModal = lazy(() => import("./ArchiveTemplateModal"));
const CropImageModal = lazy(() => import("./CropImageModal"));
const DiscardUnsavedChangesModal = lazy(
  () => import("./DiscardUnsavedChangesModal"),
);
const SaveTemplateModal = lazy(() => import("./SaveTemplateModal"));

interface ScheduledBriefingsModalsProps {
  archiveTemplate?: ArchiveTemplateModalProps;
  cropImage?: CropImageModalProps;
  discardUnsavedChanges?: DiscardUnsavedChangesModalProps;
  saveTemplate?: SaveTemplateModalProps;
}

/**
 * Scheduled Briefings modals (lazy-loaded; same idea as HeraldChatModals).
 * Each page mounts this once and passes props for the modals it uses, because
 * their state comes from page hooks such as `useBlocker` and `useTemplateStudioSave`.
 * Each modal has its own boundary, so one loading later doesn't hide another that is open.
 */
const ScheduledBriefingsModals = ({
  archiveTemplate,
  cropImage,
  discardUnsavedChanges,
  saveTemplate,
}: ScheduledBriefingsModalsProps) => (
  <>
    {archiveTemplate ? (
      <Suspense fallback={null}>
        <ArchiveTemplateModal {...archiveTemplate} />
      </Suspense>
    ) : null}
    {cropImage ? (
      <Suspense fallback={null}>
        {/* Keyed by image, so each image opens with its own shape and box. */}
        <CropImageModal key={cropImage.image.src} {...cropImage} />
      </Suspense>
    ) : null}
    {discardUnsavedChanges ? (
      <Suspense fallback={null}>
        <DiscardUnsavedChangesModal {...discardUnsavedChanges} />
      </Suspense>
    ) : null}
    {saveTemplate ? (
      <Suspense fallback={null}>
        <SaveTemplateModal {...saveTemplate} />
      </Suspense>
    ) : null}
  </>
);

export default ScheduledBriefingsModals;
